import type { ButtonProps } from './button';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { Fragment } from 'react';

import { Button } from './button';

type FooterButtonProps = Omit<ButtonProps, 'size' | 'variant'>;

export type FooterProps = {
  mobileMode?: 'fullwidth' | 'stacked';
  size?: ButtonProps['size'];
  container?: React.ComponentType<{
    asChild?: boolean;
  }>;
} & (
  | {
      cancel: FooterButtonProps;
      confirm: FooterButtonProps;
      secondary: FooterButtonProps;
    }
  | {
      cancel: FooterButtonProps;
      confirm: FooterButtonProps;
      secondary?: never;
    }
  | {
      cancel?: never;
      confirm: FooterButtonProps;
      secondary?: never;
    }
);

export const FooterButtons = ({
  container,
  confirm,
  cancel,
  mobileMode,
  secondary,
  size = 'lg',
}: FooterProps) => {
  const footerButtons = [confirm, cancel, secondary].filter(Boolean);
  const buttonCount = footerButtons.length;

  const hasOneButton = buttonCount === 1;
  const hasTwoButtons = buttonCount === 2;
  const hasAllButtons = buttonCount === 3;

  const Container = container ?? Fragment;

  return (
    <div
      className={cn(
        'w-full gap-4',
        { 'flex flex-1 justify-center': hasOneButton },
        {
          'flex flex-1 flex-row-reverse justify-between':
            hasTwoButtons && mobileMode !== 'stacked',
        },
        {
          'flex flex-1 flex-col md:flex-row-reverse md:justify-between':
            hasTwoButtons && mobileMode === 'stacked',
        },
        {
          'grid grid-cols-4 grid-rows-2 md:row-start-1 md:grid-cols-6 md:grid-rows-1':
            hasAllButtons,
        }
      )}
    >
      {!!confirm && (
        <Button
          className={cn(
            {
              'col-span-2 md:col-span-1 md:col-start-6 md:row-start-1':
                hasAllButtons,
            },
            { 'w-full md:w-auto': !!mobileMode && hasTwoButtons }
          )}
          size={size}
          {...confirm}
          variant="default"
        />
      )}

      {!!secondary && (
        <Button
          className={cn({
            'col-span-2 md:col-span-1 md:col-start-5 md:row-start-1':
              hasAllButtons,
          })}
          size={size}
          variant="secondary"
          {...secondary}
        />
      )}

      {!!cancel && (
        <Container asChild>
          <Button
            className={cn(
              { 'col-span-full md:col-span-1 md:col-start-1': hasAllButtons },
              { 'w-full md:w-auto': !!mobileMode && hasTwoButtons }
            )}
            size={size}
            variant="outline"
            {...cancel}
          />
        </Container>
      )}
    </div>
  );
};
